import {snapshotUnavailableShort} from "../../utils/reportingSnapshotAvailability.js";
import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {snapshotHasPublishedDays, snapshotPeriodValue, snapshotRowsForPublishedDays} from "../../utils/reportingSnapshotAvailability.js";
import {
    buildChannelRow,
    channelRowName,
    dividePercent,
    divideRatio,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartMultiplier,
    formatChartNumber,
    KpiCard,
    MetricHelpLabel,
    MetricBasis,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    snapshotCompletenessAlert,
} from "./ReportingLab.jsx";
import {hasReturnedMoneyMetric, purchaseCostQuality} from "../../utils/reportingFinancialCompleteness.js";
import {calculatePnoPercent, nullableMoneyMajorUnitAmount} from "../../utils/reportingMetricRatios.js";
import {poasUnavailableCopy, poasUnavailableReasons} from "../../utils/reportingMetricPresentation.js";
import {assignedMarketingChartPoint, partialProfitabilityChartPoint, partialProfitabilityMarginPercent, selectAssignedMarketingMetric, selectProfitabilityMetric, selectReportProfitabilityMetric} from "../../utils/reportingPartialProfitability.js";
import ReportingCostCoverageContext from "../../components/reporting/ReportingCostCoverageContext.jsx";
import {ProfitabilityMetricLabel, ProfitabilityMetricTooltip} from "../../components/reporting/ProfitabilityMetricTooltip.jsx";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import {marketingDisplayChannel} from "./ReportingMarketingLab.jsx";
import CentreSpendAttributionNotice, {selectedCentreSpendAttributionStatus} from "./CentreSpendAttributionNotice.jsx";
import {hasPurchaseCostCoverageContext, shouldHidePurchaseCostCoverage} from "../../utils/purchaseCostCoveragePresentation.js";

export function buildProfitabilityChannelRow(channel, overview, t, {snapshotIncomplete = false} = {}) {
    const row = buildChannelRow(marketingDisplayChannel(channel, t), overview, MetricBasis.Completed);
    const partialProfitability = overview?.partialProfitability;
    const assignedResult = snapshotIncomplete ? null : partialProfitability?.assignedMarketingResult;
    const assigned = ["full", "known_lines"].includes(assignedResult?.grossProfitBasis) ? assignedResult : null;
    const assignedSpendAmount = assignedResult?.assignedMarketingSpend?.amount;
    const assignedSpendAvailable = assignedSpendAmount !== null && assignedSpendAmount !== undefined && Number.isFinite(Number(assignedSpendAmount));
    const poasSelection = selectAssignedMarketingMetric({
        standardSelection: {value: row.poas, partial: row.poasIsPartial, allowed: row.poas !== undefined},
        assignedMarketingResult: assigned,
        assignedField: "poas",
        snapshotIncomplete,
    });
    return assigned ? {
        ...row,
        partialProfitability,
        profit: assigned.profitAfterAssignedMarketing ?? null,
        contributionBeforeMarketing: assigned.grossProfit ?? null,
        poas: poasSelection.value ?? null,
        ...(assignedSpendAvailable ? {
            pno: calculatePnoPercent(assignedSpendAmount, row.turnover?.amount),
            pnoUsesAssignedMarketing: true,
        } : {}),
        profitIsPartial: true,
        grossProfitIsPartial: true,
        poasIsPartial: poasSelection.partial,
        profitUsesAssignedMarketing: true,
        poasUsesAssignedMarketing: poasSelection.assignedMarketing,
        margin: null,
        marginIsPartial: false,
    } : assignedSpendAvailable ? {
        ...row,
        partialProfitability,
        pno: calculatePnoPercent(assignedSpendAmount, row.turnover?.amount),
        pnoUsesAssignedMarketing: true,
    } : row;
}

export function ProfitabilityMarginMeta({margin, renderPercentValue}) {
    const {t} = useTranslation();
    return <span className="inline-flex items-center gap-1">{t("reporting_lab.kpi.margin", {defaultValue: "Marže po reklamě (%)"})} <span className={margin !== null && margin !== undefined && Number(margin) < 0 ? "text-datixo-negative" : undefined}>{renderPercentValue(margin)}</span></span>;
}

export function profitabilityChannelMetricAvailable(row, metric, adMetricsAvailable) {
    if (metric === "pno" && row?.pnoUsesAssignedMarketing) return true;
    if (metric === "poas" && row?.poasUsesAssignedMarketing) return true;
    return adMetricsAvailable;
}

export {ProfitabilityMetricLabel, ProfitabilityMetricTooltip} from "../../components/reporting/ProfitabilityMetricTooltip.jsx";

export function purchaseCostCardPresentation({periodIncomplete, costComplete, hasAmount, amount, formattedAmount, knownLineCount, knownSubtotalLabel, periodUnavailableLabel}) {
    const hasCount = knownLineCount !== null && knownLineCount !== undefined && knownLineCount !== "" && Number.isFinite(Number(knownLineCount));
    const hasKnownLines = hasCount && Number(knownLineCount) > 0;
    const emptyKnownCohort = knownLineCount !== null && knownLineCount !== undefined && knownLineCount !== "" && Number.isFinite(Number(knownLineCount)) && Number(knownLineCount) === 0;
    const unavailable = periodIncomplete || !hasAmount || amount == null || (costComplete === false && emptyKnownCohort);
    const value = unavailable ? "—" : (formattedAmount ?? "—");
    const info = periodIncomplete
        ? periodUnavailableLabel
        : costComplete === false && emptyKnownCohort
            ? knownSubtotalLabel
        : costComplete
            ? null
            : (hasKnownLines || !hasCount) && hasAmount && amount != null
                ? `${knownSubtotalLabel}: ${formattedAmount}`
                : knownSubtotalLabel;

    return {value, info};
}

export function profitabilityPurchaseCostCardMetric({costComplete, fullAmount, partialProfitability}) {
    if (costComplete === true) {
        return {amount: fullAmount, hasAmount: fullAmount !== undefined && fullAmount !== null, knownLineCount: partialProfitability?.knownLineCount};
    }
    const knownLineCount = partialProfitability?.knownLineCount;
    const hasKnownLineCount = knownLineCount !== null && knownLineCount !== undefined && knownLineCount !== "" && Number.isFinite(Number(knownLineCount));
    if (hasKnownLineCount && (partialProfitability?.available === true || Number(knownLineCount) === 0)) {
        const amount = partialProfitability.knownPurchaseCost;
        return {amount, hasAmount: amount !== undefined && amount !== null, knownLineCount};
    }
    return {amount: fullAmount, hasAmount: fullAmount !== undefined && fullAmount !== null, knownLineCount};
}

export function selectedCentreProfitabilityAttributionState(attribution, selectedCentreFilter) {
    const status = selectedCentreFilter
        ? selectedCentreSpendAttributionStatus(attribution, true)
        : null;
    return {status, adMetricsAvailable: status === null};
}

export function selectedCentreAttributionUnavailableCopy(status, t) {
    if (status !== "incomplete") {
        return null;
    }

    return {
        short: t("reporting_lab.centre_spend_attribution.row_partial_short", {defaultValue: "Nepřiřazená reklama"}),
        detail: t("reporting_lab.centre_spend_attribution.row_partial_help", {defaultValue: "Část reklamních nákladů není přiřazená ke středisku."}),
    };
}

export function profitabilityBridgeAvailable({periodIncomplete, hasTurnover, hasPurchaseCost}) {
    return !periodIncomplete && (hasTurnover || hasPurchaseCost);
}

export function profitabilityBridgeUnavailableMessageVisible({loading, bridgeAvailable}) {
    return !loading && !bridgeAvailable;
}

export function profitabilityComponentValues({turnover, spendGoods, spendMarketing, grossProfit, profit, profitAvailable, adMetricsAvailable}) {
    const cost = nullableMoneyMajorUnitAmount(spendGoods);
    const advertising = nullableMoneyMajorUnitAmount(spendMarketing);
    return [
        nullableMoneyMajorUnitAmount(turnover),
        cost === null ? null : -cost,
        grossProfit === null || grossProfit === undefined ? null : grossProfit / 100,
        adMetricsAvailable && advertising !== null ? -advertising : null,
        profitAvailable ? nullableMoneyMajorUnitAmount(profit) : null,
    ];
}

function hasUsableMoneyAmount(value) {
    return value?.amount !== null && value?.amount !== undefined && value?.amount !== "" && Number.isFinite(Number(value.amount));
}

export function assignedMarketingBridgeValues({assignedMarketingResult, partialProfitability, fullTurnover, fullSpendGoods, snapshotIncomplete = false}) {
    if (snapshotIncomplete || !assignedMarketingResult || !hasUsableMoneyAmount(assignedMarketingResult.grossProfit)
        || !hasUsableMoneyAmount(assignedMarketingResult.assignedMarketingSpend)
        || !hasUsableMoneyAmount(assignedMarketingResult.profitAfterAssignedMarketing)) return null;

    let turnover;
    let spendGoods;
    if (assignedMarketingResult.grossProfitBasis === "known_lines") {
        turnover = partialProfitability?.knownRevenue;
        spendGoods = partialProfitability?.knownPurchaseCost;
    } else if (assignedMarketingResult.grossProfitBasis === "full") {
        turnover = fullTurnover;
        spendGoods = fullSpendGoods;
    } else {
        return null;
    }

    return profitabilityComponentValues({
        turnover,
        spendGoods,
        spendMarketing: assignedMarketingResult.assignedMarketingSpend,
        grossProfit: Number(assignedMarketingResult.grossProfit.amount),
        profit: assignedMarketingResult.profitAfterAssignedMarketing,
        profitAvailable: true,
        adMetricsAvailable: true,
    });
}

export function ProfitabilityDataNotice({partialProfitability, attribution, selectedCentreFilter = false, loading = false}) {
    const {t} = useTranslation();
    const attributionStatus = selectedCentreFilter ? selectedCentreSpendAttributionStatus(attribution, true) : null;
    const attributionHelp = attributionStatus === "incomplete"
        ? selectedCentreAttributionUnavailableCopy(attributionStatus, t)?.detail
        : attributionStatus === "unknown"
            ? t("reporting_lab.centre_spend_attribution.channel_unknown_help", {defaultValue: "Odpověď neobsahuje údaje o úplnosti přiřazení. Hodnoty kanálů mohou být částečné; další reklamní náklady nemají jednoznačné středisko. Úplný PNO a zisk po reklamě proto nelze určit."})
            : undefined;
    const showStandaloneAttribution = Boolean(attributionStatus)
        && (!hasPurchaseCostCoverageContext(partialProfitability)
            || shouldHidePurchaseCostCoverage(partialProfitability?.revenueCoveragePercent, partialProfitability?.actionableUnknownLineCount));
    return <>
        <ReportingCostCoverageContext data={partialProfitability} reportContext="profitability" additionalHelp={attributionHelp} loading={loading}/>
        {showStandaloneAttribution && <CentreSpendAttributionNotice attribution={attribution} selectedCentreFilter loading={loading}/>}
    </>;
}

export function hasVerifiedEmptyPurchaseCohort({snapshot, performance, partialProfitability}) {
    const isExplicitZero = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) && Number(value) === 0;
    return snapshot?.complete === true
        && performance?.orderAmountCompleted === 0
        && isExplicitZero(performance?.orderTurnoverNet?.amount)
        && isExplicitZero(performance?.orderSpendGoodsNet?.amount)
        && performance?.purchaseCostQualityAvailable === true
        && performance?.purchaseCostCompleteNet === true
        && isExplicitZero(performance?.purchaseCostUnknownNet)
        && partialProfitability?.complete === true
        && partialProfitability?.available === false
        && isExplicitZero(partialProfitability?.knownLineCount)
        && isExplicitZero(partialProfitability?.unknownLineCount)
        && isExplicitZero(partialProfitability?.estimatedLineCount);
}

export function profitabilityRowTooltipPosition(index, rowCount) {
    return rowCount > 0 && index >= rowCount - 2 ? "top" : "bottom";
}

export default function ReportingProfitabilityLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const renderRatioValue = (value) => formatChartMultiplier(value);
    const [shopPerformance, setShopPerformance] = useState(null);
    const [channelData, setChannelData] = useState([]);
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");
    const selectedCentreFilter = (centresAllowListed ?? []).length > 0;

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setShopPerformance(null);
        setChannelData([]);
        setSnapshot(null);

        const erpApi = new ErpCalculationApi(getAuthorityConfiguration());

        erpApi.reportingOverview({projectId: project.id, erpFilter})
            .then((result) => {
                if (!active) {
                    return;
                }

                setShopPerformance(result.shopPerformance);
                setChannelData((result.channels?.items ?? []).map((item) => ({
                    channel: {id: item.id, name: item.name},
                    overview: item.overview,
                })));
                setSnapshot(result.snapshot);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter]);

    const channelRows = useMemo(() => {
        const snapshotUnavailable = !snapshotHasPublishedDays(snapshot);
        const rows = channelData
            .map(({channel, overview}) => buildProfitabilityChannelRow(channel, overview, t, {snapshotIncomplete: snapshotUnavailable}))
            .filter((row) => moneyAmount(row.spendMarketing) > 0 || moneyAmount(row.turnover) > 0 || row.orders > 0)
            .sort((a, b) => moneyAmount(b.profit) - moneyAmount(a.profit));
        return snapshotRowsForPublishedDays(rows, snapshot);
    }, [channelData, snapshot, t]);

    const periodIncomplete = !snapshotHasPublishedDays(snapshot);
    const attributionState = selectedCentreProfitabilityAttributionState(shopPerformance?.centreSpendAttribution, selectedCentreFilter);
    const attributionStatus = attributionState.status;
    const adMetricsAvailable = !periodIncomplete && attributionState.adMetricsAvailable;
    const turnover = snapshotPeriodValue(shopPerformance?.orderTurnoverNet, snapshot);
    const fullProfit = snapshotPeriodValue(shopPerformance?.orderProfitNet, snapshot);
    const spendMarketing = snapshotPeriodValue(shopPerformance?.orderSpendMarketing, snapshot);
    const partialProfitability = periodIncomplete ? undefined : shopPerformance?.partialProfitability;
    const emptyCompletedPurchaseCohort = hasVerifiedEmptyPurchaseCohort({snapshot, performance: shopPerformance, partialProfitability});
    const purchaseCostContextPartialProfitability = emptyCompletedPurchaseCohort ? undefined : partialProfitability;
    const spendGoods = snapshotPeriodValue(shopPerformance?.orderSpendGoodsNet, snapshot);
    const costQuality = purchaseCostQuality(shopPerformance ? [shopPerformance] : [], MetricBasis.Completed);
    const purchaseCostMetric = profitabilityPurchaseCostCardMetric({costComplete: costQuality.complete, fullAmount: spendGoods, partialProfitability});
    const standardProfitPresentation = selectProfitabilityMetric({fullValue: shopPerformance?.orderProfitNet === undefined ? undefined : fullProfit, fullComplete: costQuality.complete, partialProfitability, partialField: "knownProfitAfterAllMarketing"});
    const profitPresentation = selectReportProfitabilityMetric({standardSelection: standardProfitPresentation, partialProfitability, assignedField: "profitAfterAssignedMarketing", snapshotIncomplete: periodIncomplete});
    const profit = profitPresentation.value;
    const fullGrossProfit = !periodIncomplete && costQuality.complete && spendGoods != null && turnover != null ? moneyAmount(turnover) - moneyAmount(spendGoods) : null;
    const standardGrossProfitPresentation = selectProfitabilityMetric({fullValue: shopPerformance?.orderProfitNet === undefined ? undefined : fullGrossProfit == null ? null : {amount: fullGrossProfit, currency: turnover?.currency}, fullComplete: costQuality.complete, partialProfitability, partialField: "knownGrossProfit"});
    const grossProfitPresentation = selectReportProfitabilityMetric({standardSelection: standardGrossProfitPresentation, partialProfitability, assignedField: "grossProfit", snapshotIncomplete: periodIncomplete});
    const marginBeforeMarketing = grossProfitPresentation.value?.amount == null ? null : Number(grossProfitPresentation.value.amount);
    const bridgeTurnover = grossProfitPresentation.partial ? partialProfitability.knownRevenue : turnover;
    const bridgeCost = grossProfitPresentation.partial ? partialProfitability.knownPurchaseCost : spendGoods;
    const purchaseCostCard = purchaseCostCardPresentation({
        periodIncomplete,
        costComplete: costQuality.complete,
        hasAmount: purchaseCostMetric.hasAmount,
        amount: purchaseCostMetric.amount,
        formattedAmount: renderCurrencyValue(purchaseCostMetric.amount, true),
        knownLineCount: purchaseCostMetric.knownLineCount,
        knownSubtotalLabel: t("reporting_lab.cost_quality.known_cost_subtotal", {defaultValue: "Známý mezisoučet nákupních cen, ne celkové COGS"}),
        periodUnavailableLabel: t("reporting_lab.snapshot.incomplete_help", {defaultValue: "Souhrny za celé období nejsou dostupné."}),
    });
    const profitAvailable = (adMetricsAvailable || profitPresentation.assignedMarketing) && profit != null;
    const margin = profitAvailable ? profitPresentation.assignedMarketing ? null : profitPresentation.partial ? partialProfitabilityMarginPercent(partialProfitability) : dividePercent(moneyAmount(profit), moneyAmount(turnover)) : null;
    const pno = adMetricsAvailable ? calculatePnoPercent(spendMarketing?.amount, turnover?.amount) : null;
    const assignedBridgeValues = profitPresentation.assignedMarketing && grossProfitPresentation.assignedMarketing
        ? assignedMarketingBridgeValues({
            assignedMarketingResult: partialProfitability?.assignedMarketingResult,
            partialProfitability,
            fullTurnover: turnover,
            fullSpendGoods: spendGoods,
            snapshotIncomplete: periodIncomplete,
        })
        : null;
    const standardBridgeAvailable = !profitPresentation.assignedMarketing && !grossProfitPresentation.assignedMarketing && profitabilityBridgeAvailable({
        periodIncomplete,
        costComplete: costQuality.complete,
        hasTurnover: turnover != null,
        hasPurchaseCost: spendGoods != null,
    });
    const bridgeAvailable = assignedBridgeValues !== null || standardBridgeAvailable;
    const bridgeUsesKnownLines = profitPresentation.assignedMarketing
        ? partialProfitability?.assignedMarketingResult?.grossProfitBasis === "known_lines"
        : grossProfitPresentation.partial;
    const bridgeValues = assignedBridgeValues ?? profitabilityComponentValues({turnover: bridgeTurnover, spendGoods: bridgeCost, spendMarketing, grossProfit: marginBeforeMarketing, profit, profitAvailable, adMetricsAvailable});
    const fullPoas = !adMetricsAvailable || fullGrossProfit === null || moneyAmount(turnover) < 0 ? null : divideRatio(fullGrossProfit, moneyAmount(spendMarketing));
    const standardPoasPresentation = selectProfitabilityMetric({fullValue: shopPerformance?.orderProfitNet === undefined ? undefined : fullPoas, fullComplete: costQuality.complete, partialProfitability: adMetricsAvailable ? partialProfitability : undefined, partialField: "knownPoas"});
    const poasPresentation = selectReportProfitabilityMetric({standardSelection: standardPoasPresentation, partialProfitability, assignedField: "poas", snapshotIncomplete: periodIncomplete});
    const poas = poasPresentation.value ?? null;
    const poasReasons = poasUnavailableReasons(shopPerformance && !periodIncomplete ? [shopPerformance] : [], {
        revenue: "orderTurnoverNet",
        spend: "orderSpendMarketing",
        costComplete: "purchaseCostCompleteNet",
        costUnknown: "purchaseCostUnknownNet",
        costEstimated: "purchaseCostEstimatedNet",
    }, poas === null);
    const poasUnavailable = attributionStatus === "unknown"
        ? {
            short: t("reporting_lab.centre_spend_attribution.channel_unknown_short", {defaultValue: "Neověřeno"}),
            detail: t("reporting_lab.centre_spend_attribution.channel_unknown_help", {defaultValue: "Odpověď neobsahuje údaje o úplnosti přiřazení. Hodnoty kanálů mohou být částečné; další reklamní náklady nemají jednoznačné středisko. Úplný PNO a zisk po reklamě proto nelze určit."}),
        }
        : attributionStatus === "incomplete"
            ? selectedCentreAttributionUnavailableCopy(attributionStatus, t)
        : periodIncomplete
        ? {
            short: snapshotUnavailableShort(snapshot, t),
            detail: t("reporting_lab.snapshot.incomplete_help", {defaultValue: "Souhrny za celé období nejsou dostupné."}),
        }
        : poasUnavailableCopy(poasReasons, t);
    const profitUnavailable = attributionStatus !== null
        ? poasUnavailable
        : periodIncomplete
            ? {
                short: snapshotUnavailableShort(snapshot, t),
                detail: t("reporting_lab.snapshot.incomplete_help", {defaultValue: "Souhrny za celé období nejsou dostupné."}),
            }
            : !costQuality.complete
                ? {
                    short: t("reporting_lab.cost_quality.incomplete_short", {defaultValue: "Neúplné náklady"}),
                    detail: t("reporting_lab.cost_quality.incomplete", {defaultValue: "Část nákladových podkladů chybí nebo není ověřena. Zisk, marži a POAS proto nelze za celé období spolehlivě vyčíslit."}),
                }
                : null;
    const currency = profit?.currency ?? turnover?.currency ?? projectCurrency(project);
    const completeChannelRows = channelRows.filter((row) => row.purchaseCostComplete === true || row.profitUsesAssignedMarketing || (row.partialProfitability?.available === true && (row.profit != null || row.contributionBeforeMarketing != null)));
    const topProfitChannels = completeChannelRows.slice(0, 8);
    const poasChannelRows = completeChannelRows.slice(0, 10);
    const lossChannels = !adMetricsAvailable ? [] : [...completeChannelRows].filter((row) => moneyAmount(row.profit) < 0 || (row.poas !== null && row.poas < 1)).sort((a, b) => moneyAmount(a.profit) - moneyAmount(b.profit)).slice(0, 6);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const profitabilityBridgeOption = {
        textStyle: chartTextStyle,
        grid: {left: 18, right: 18, top: 24, bottom: 42, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        xAxis: {
            type: "category",
            data: [
                bridgeUsesKnownLines ? t("reporting_lab.profitability.evaluated_revenue", {defaultValue: "Vyhodnocený obrat"}) : t("reporting_lab.kpi.revenue", {defaultValue: "Obrat"}),
                t("reporting_lab.table.purchase", {defaultValue: "Nákup"}),
                t("reporting_lab.product_profitability.margin_before_marketing_short", {defaultValue: "Hrubý zisk"}),
                t("reporting_lab.table.marketing", {defaultValue: "Marketing"}),
                t("reporting_lab.profitability.after_advertising", {defaultValue: "Po reklamě"}),
            ],
            axisLabel: {fontSize: 12, interval: 0},
        },
        yAxis: {type: "value", axisLabel: {fontSize: 12, formatter: formatChartNumber}},
        series: [{
            type: "bar",
            data: bridgeValues
                .map((value, index) => ({value, itemStyle: {color: ["#2563eb", "#f97316", "#0ea5e9", "#7c3aed", moneyAmount(profit) < 0 ? "#e11d48" : "#16a34a"][index]}})),
            barMaxWidth: 64,
            label: {show: true, position: "top", formatter: (params) => formatChartCurrency(params.value, currency)},
        }],
    };

    const profitByChannelOption = {
        color: ["#16a34a", "#7c3aed"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 24, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {type: "category", data: topProfitChannels.map((row) => channelRowName(row, channelRows))},
        yAxis: {
            type: "value",
            axisLabel: {
                fontSize: 12,
                formatter: formatChartNumber,
            },
        },
        series: [
            {
                name: t("reporting_lab.series.profit", {defaultValue: "Hrubý zisk po reklamě"}),
                type: "bar",
                data: topProfitChannels.map((row) => row.profitUsesAssignedMarketing ? assignedMarketingChartPoint(nullableMoneyMajorUnitAmount(row.profit), row.partialProfitability, t) : partialProfitabilityChartPoint(nullableMoneyMajorUnitAmount(row.profit), row.partialProfitability, row.profitIsPartial, t)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.series.marketing", {defaultValue: "Marketing"}),
                type: "bar",
                data: topProfitChannels.map((row) => moneyToMajor(row.profitUsesAssignedMarketing ? row.partialProfitability?.assignedMarketingResult?.assignedMarketingSpend : row.spendMarketing)),
                barMaxWidth: 28,
            },
        ],
    };

    const poasOption = {
        color: ["#2563eb"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 20, bottom: 54, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, formatChartMultiplier),
        },
        xAxis: {type: "category", data: poasChannelRows.map((row) => channelRowName(row, channelRows))},
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartMultiplier},
        },
        series: [
            {
                name: "POAS",
                type: "bar",
                data: poasChannelRows.map((row) => (adMetricsAvailable || row.poasUsesAssignedMarketing) ? row.poasUsesAssignedMarketing ? assignedMarketingChartPoint(row.poas, row.partialProfitability, t, "poas") : partialProfitabilityChartPoint(row.poas, row.partialProfitability, row.poasIsPartial, t) : null),
                barMaxWidth: 30,
                markLine: {
                    symbol: "none",
                    data: [{yAxis: 1, name: "Break-even"}],
                    label: {formatter: "Break-even 1,0×"},
                },
            },
        ],
    };

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.profitability", {defaultValue: "Ziskovost"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[snapshotCompletenessAlert(snapshot, t)]}
     intro={<ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.profitability.title", {defaultValue: "K čemu slouží hrubý zisk po reklamě"})}
                >
                    {t("reporting_lab.descriptions.profitability.text", {defaultValue: "Manažerský pohled na dokončený obrat, hrubý zisk a hrubý zisk po reklamě včetně kanálů, které zvyšují nebo snižují výsledek. Nezahrnuje interní náklady dopravy, plateb ani režii."})}
                </ReportingPageDescription>}>
        <div className="grid grid-cols-12 gap-4">
                <ReportingSnapshotStatus snapshots={snapshot}/>
                <ProfitabilityDataNotice quality={costQuality} partialProfitability={purchaseCostContextPartialProfitability} attribution={shopPerformance?.centreSpendAttribution} selectedCentreFilter={selectedCentreFilter} periodIncomplete={periodIncomplete} loading={loading}/>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.revenue", {defaultValue: "Obrat"})} value={renderCurrencyValue(turnover, true)} meta={periodIncomplete ? snapshotUnavailableShort(snapshot, t) : null}/>
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={profitPresentation.partial ? t("reporting_lab.kpi.partial_profit", {defaultValue: "Dílčí hrubý zisk po reklamě"}) : t("reporting_lab.kpi.profit", {defaultValue: "Hrubý zisk po reklamě"})} value={profitAvailable ? renderCurrencyValue(profit, true) : "—"} meta={profitAvailable ? null : profitUnavailable?.short ?? null} metaTone={!profitAvailable && profitUnavailable ? "unavailable" : "default"} info={<ProfitabilityMetricTooltip metric="profit" partialProfitability={partialProfitability} assignedResult={profitPresentation.assignedMarketing ? partialProfitability?.assignedMarketingResult : null} quality={partialProfitability?.quality} attributionStatus={attributionStatus} costQuality={costQuality} t={t}/>} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})} tone={profitAvailable && moneyAmount(profit) < 0 ? "bad" : "default"}/>
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard
                        loading={loading}
                        label={t("reporting_lab.table.purchase", {defaultValue: "Nákup"})}
                        value={purchaseCostCard.value}
                        meta={periodIncomplete
                            ? snapshotUnavailableShort(snapshot, t)
                            : costQuality.complete
                                ? t("reporting_lab.profitability.purchase_price_goods", {defaultValue: "Nákupní cena zboží"})
                                : t("reporting_lab.partial_profitability.known_purchase_prices", {defaultValue: "Známé nákupní ceny"})}
                        metaTone={periodIncomplete || !costQuality.complete ? "unavailable" : "default"}
                        info={purchaseCostCard.info}
                        infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}
                    />
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard
                        loading={loading}
                        label="POAS"
                        value={renderRatioValue(poas)}
                        meta={<>{t("reporting_lab.marketing.poas_basis_short", {defaultValue: "Hrubý zisk před reklamou / marketingové náklady"})}{!poasPresentation.partial && poasUnavailable.short ? ` · ${poasUnavailable.short}` : ""}</>}
                        metaTone={poas === null && poasUnavailable.short ? "unavailable" : "default"}
                        info={<>
                            <div><ProfitabilityMetricTooltip metric="poas" partialProfitability={partialProfitability} assignedResult={poasPresentation.assignedMarketing ? partialProfitability?.assignedMarketingResult : null} quality={partialProfitability?.quality} attributionStatus={attributionStatus} costQuality={costQuality} t={t}/></div>
                            {!poasPresentation.partial && poasUnavailable.detail && <div className="mt-1">{poasUnavailable.detail}</div>}
                        </>}
                        infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}
                        tone={poas !== null && poas < 1 ? "bad" : "default"}
                    />
                </div>

                <Panel loading={loading} title={<ProfitabilityMetricLabel label={t("reporting_lab.profitability.financial_bridge", {defaultValue: "Složky výsledku"})} metric="profit" partialProfitability={partialProfitability} assignedResult={profitPresentation.assignedMarketing ? partialProfitability?.assignedMarketingResult : null} quality={partialProfitability?.quality} attributionStatus={attributionStatus} costQuality={costQuality}/>} className="col-span-12">
                    {bridgeAvailable
                        ? <EChart option={profitabilityBridgeOption} className="h-80"/>
                        : profitabilityBridgeUnavailableMessageVisible({loading, bridgeAvailable})
                            ? <div className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">{profitPresentation.assignedMarketing ? t("reporting_lab.partial_profitability.assigned_bridge_unavailable", {defaultValue: "Rozklad výsledku není dostupný, protože přiřazené náklady na reklamu nepokrývají celý vybraný rozsah."}) : profitUnavailable?.detail ?? t("reporting_lab.cost_quality.chart_unavailable", {defaultValue: "Rozklad výsledku není dostupný, protože část nákupních cen chybí nebo není ověřená."})}</div>
                            : null}
                </Panel>

                <Panel loading={loading} title={<ProfitabilityMetricLabel label={t("reporting_lab.profitability.profit_by_channel", {defaultValue: "Hrubý zisk po reklamě a marketing podle kanálu"})} metric="profit" partialProfitability={partialProfitability} assignedResult={profitPresentation.assignedMarketing ? partialProfitability?.assignedMarketingResult : null} quality={partialProfitability?.quality} attributionStatus={attributionStatus} costQuality={costQuality}/>} className="col-span-12 xl:col-span-7">
                    <EChart option={profitByChannelOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={<ProfitabilityMetricLabel label={t("reporting_lab.profitability.poas_by_channel", {defaultValue: "POAS podle kanálu"})} metric="poas" partialProfitability={partialProfitability} assignedResult={poasPresentation.assignedMarketing ? partialProfitability?.assignedMarketingResult : null} quality={partialProfitability?.quality} attributionStatus={attributionStatus} costQuality={costQuality}/>} className="col-span-12 xl:col-span-5">
                    <EChart option={poasOption} className="h-96"/>
                </Panel>

                {adMetricsAvailable && <Panel loading={loading} title={t("reporting_lab.profitability.channels_to_check", {defaultValue: "Kanály ke kontrole výsledku po reklamě"})} className="col-span-12 xl:col-span-4">
                    <div className="space-y-3">
                        {lossChannels.map((row) => <div key={row.channel.id} className="app-surface rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                            <div className="flex items-center justify-between gap-3">
                                <div className="font-semibold text-slate-900 dark:text-slate-100">{channelRowName(row, channelRows)}</div>
                                <div className="text-sm font-semibold text-datixo-negative">{renderRatioValue(row.poas)}</div>
                            </div>
                            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-500 dark:text-slate-400">
                                <div>{t("reporting_lab.kpi.marketing_spend", {defaultValue: "Marketingové náklady"})} {renderCurrencyValue(row.spendMarketing, true)}</div>
                                <div className="text-right">{t("reporting_lab.review.profit", {defaultValue: "Hrubý zisk po reklamě"})} {renderCurrencyValue(row.profit, true)}</div>
                            </div>
                        </div>)}
                        {lossChannels.length === 0 && <div className="text-sm text-slate-500 dark:text-slate-400">{completeChannelRows.length === 0
                            ? t("reporting_lab.profitability.channels_unavailable", {defaultValue: "Výsledek nelze pro toto období u kanálů vyhodnotit z dostupných údajů."})
                            : t("reporting_lab.profitability.no_loss_channels", {defaultValue: "U vyhodnotitelných kanálů nejsou pro vybrané období vidět ztrátové nebo podlimitní výsledky."})}</div>}
                    </div>
                </Panel>}

                <Panel loading={loading} title={<ProfitabilityMetricLabel label={t("reporting_lab.profitability.table", {defaultValue: "Hrubý zisk po reklamě podle kanálu"})} metric="profit" partialProfitability={partialProfitability} assignedResult={profitPresentation.assignedMarketing ? partialProfitability?.assignedMarketingResult : null} quality={partialProfitability?.quality} attributionStatus={attributionStatus} costQuality={costQuality}/>} className="col-span-12 xl:col-span-8">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.table.channel", {defaultValue: "Kanál"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.table.revenue", {defaultValue: "Obrat"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold"><ProfitabilityMetricLabel label={t("reporting_lab.table.contribution", {defaultValue: "Hrubý zisk po reklamě"})} metric="profit" partialProfitability={partialProfitability} assignedResult={profitPresentation.assignedMarketing ? partialProfitability?.assignedMarketingResult : null} quality={partialProfitability?.quality} attributionStatus={attributionStatus} costQuality={costQuality}/></th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.table.marketing", {defaultValue: "Marketing"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold"><ProfitabilityMetricLabel label={t("reporting_lab.kpi.margin", {defaultValue: "Marže po reklamě (%)"})} metric="profit" partialProfitability={partialProfitability} assignedResult={profitPresentation.assignedMarketing ? partialProfitability?.assignedMarketingResult : null} quality={partialProfitability?.quality} attributionStatus={attributionStatus} costQuality={costQuality}/></th>
                                <th className="py-2 pr-4 text-right font-semibold"><MetricHelpLabel label="PNO" tooltip={<>{t("reporting_lab.metrics_help.pno", {defaultValue: "Náklady na reklamu v poměru k obratu. PNO není násobek; při nulovém obratu není definováno."})}{attributionStatus !== null && poasUnavailable?.detail && <div className="mt-1">{poasUnavailable.detail}</div>}</>}/></th>
                                <th className="py-2 text-right font-semibold"><ProfitabilityMetricLabel label="POAS" metric="poas" partialProfitability={partialProfitability} assignedResult={poasPresentation.assignedMarketing ? partialProfitability?.assignedMarketingResult : null} quality={partialProfitability?.quality} attributionStatus={attributionStatus} costQuality={costQuality}/></th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {channelRows.map((row) => <tr key={row.channel.id}>
                                <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{channelRowName(row, channelRows)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.turnover, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.profit, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.spendMarketing, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{adMetricsAvailable ? renderPercentValue(row.margin) : "—"}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{profitabilityChannelMetricAvailable(row, "pno", adMetricsAvailable) ? renderPercentValue(row.pno) : "—"}</td>
                                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{profitabilityChannelMetricAvailable(row, "poas", adMetricsAvailable) ? renderRatioValue(row.poas) : "—"}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>
    </WideSecureLayout>;
}
